import './GenerateQuestion.scss';

import * as React from 'react';

import type {
  AiJobStatusResponse,
  GenerateQuestionsResponse,
} from '~api/ai/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { translate } from '~l10n/client';
import { createSetAtom } from '~shared/atoms/utils/createSetAtom';
import { Badge } from '~shared/components/Badge';
import {
  IconButton,
  SmallButton,
  SmallLinkButton,
} from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/large';
import { IconExternal } from '~shared/components/icons/standard';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { usePollHistory } from '~shared/polling';
import { Error as ErrorIcon } from '~teacher/components/ExportReports/ExportFeedback/ExportFeedbackIcons';
import { AIQuestionsLoadingSpinner } from '~teacher/components/QuizEditor/AIQuestionsLoadingSpinner';
import { useQuizContext } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import {
  useAddAllQuestions,
  useAddQuestion,
} from '~teacher/components/QuizEditor/shared/AiQuestions';
import { useAccount } from '~teacher/hook/account';
import { addQuestionIds } from '~teacher/polling/utils';

import { generateQuestionAtom, positionAtom, useScopedStepAtom } from './atom';
import { useQuestionGenerator } from './context';
import { ExpandableQuestionCard } from './ExpandableQuestionCard';
import { LearnMoreIcon } from './LearnMoreIcon';
import { RateQuestionQuality } from './RateQuestionQuality';

export const addedQuestionSetAtom = createSetAtom<string>(new Set<string>());

interface Props {
  onClose: () => void;
  refreshQuestions: () => Promise<void>;
}

export function GenerateQuestionStep2({ onClose, refreshQuestions }: Props) {
  const [, setStep] = useScopedStepAtom();
  const questionGenerator = useQuestionGenerator();
  const quizContext = useQuizContext();
  const appContext = useTeacherApplicationContext();
  const resetAddedQuestions = useSetAtom(addedQuestionSetAtom.resetAtom);
  const addToAddedQuestionSet = useSetAtom(addedQuestionSetAtom.addAtom);
  const addedQuestionSet = useAtomValue(addedQuestionSetAtom.valueAtom);
  const dispatchGenerateQuestion = useSetAtom(generateQuestionAtom);
  const [position] = useAtom(positionAtom);
  const requestId = questionGenerator.data?.requestId;
  const isAsync = questionGenerator.data?.async;

  const { data: asyncData, cleanup } = usePollHistory<
    AiJobStatusResponse['data']
  >({
    requestId: isAsync ? requestId : null,
    cleanupOnUnmount: true,
  });

  // Get questions from sync or async response
  const displayQuestions = React.useMemo(() => {
    if (!isAsync) {
      return questionGenerator.data?.questions ?? [];
    }
    if (asyncData?.questions) {
      return addQuestionIds(asyncData.questions);
    }
    return [];
  }, [isAsync, questionGenerator.data, asyncData]);

  const { addAllQuestions, isAddingAllQuestions } = useAddAllQuestions({
    quizId: quizContext.quizId,
    position,
    questions: displayQuestions,
    addedQuestionIds: addedQuestionSet,
    onQuestionAdded: addToAddedQuestionSet,
    onSuccess: async () => {
      cleanup();
      await refreshQuestions();
      onClose();
    },
    onError: () => refreshQuestions(),
    uiSource: 'ai_generate',
  });

  return (
    <>
      <div className="pb-4">
        <div className="flex gap-4">
          <div className="flex flex-col">
            <IconButton
              aria-label={translate('Back')}
              onClick={() => {
                // Reset data when going back
                questionGenerator.reset();
                // Reset added questions
                resetAddedQuestions();
                // Clear fileKey to prevent it persisting to next generation
                dispatchGenerateQuestion({ type: 'RemoveFileKey' });
                setStep({ type: 'PreviousStep' });
              }}
            >
              <IconChevron direction="left" />
            </IconButton>
          </div>
          <div className="flex flex-shrink flex-col">
            <div className="flex">
              <a
                href="https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Badge textTransform="capitalize" tone="ai">
                  {translate('AI Assisted')}
                  <LearnMoreIcon />
                </Badge>
              </a>
            </div>
            <div className="font-display text-md leading-8 text-blue-900 font-700">
              {translate('Generate Questions')}
            </div>
            <div className="text-sans text-base leading-8 text-blue-600">
              {translate(
                'Add questions to your quiz. When you are done, edit the questions on your quiz.'
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex flex-col h-full justify-between min-h-0"
        style={{
          boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
        }}
      >
        <GenerateQuestionStep2Content />
        <div
          style={{
            boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
          }}
          className="flex justify-between items-center gap-3 pt-5"
        >
          {appContext === TeacherApplicationContext.Socrative && (
            <AiFeedbackFormLink />
          )}
          <div className="flex gap-3 ml-auto">
            <SmallButton
              isDisabled={isAddingAllQuestions || displayQuestions.length === 0}
              appearance="ghost"
              onClick={() => {
                addAllQuestions();
              }}
            >
              {translate('Add All Questions')}
            </SmallButton>
            <SmallButton
              appearance="primary"
              onClick={() => {
                cleanup();
                onClose();
              }}
            >
              {translate('Done')}
            </SmallButton>
          </div>
        </div>
      </div>
    </>
  );
}

function AiFeedbackFormLink() {
  const accountQuery = useAccount();
  const account = accountQuery.data;
  const AI_FEEDBACK_FORM_URL = new URL(
    'https://showbie.typeform.com/to/IkH41IWD#' +
      new URLSearchParams({
        email: account?.email,
        name: account?.firstName,
        user_id: account?.publicId,
      }).toString()
  );

  return (
    <SmallLinkButton
      href={AI_FEEDBACK_FORM_URL.href}
      target="_blank"
      appearance="text"
    >
      {translate('Help Us Improve')}
      <IconExternal
        className="text-cyan-700"
        title={translate('External Link')}
        titleId="help-us-improve-external-link"
      />
    </SmallLinkButton>
  );
}

function GenerateQuestionStep2Loading() {
  const [, setStep] = useScopedStepAtom();
  const questionGenerator = useQuestionGenerator();
  const resetAddedQuestions = useSetAtom(addedQuestionSetAtom.resetAtom);
  const dispatchGenerateQuestion = useSetAtom(generateQuestionAtom);
  const [{ fileKey }] = useAtom(generateQuestionAtom);

  const handleGoBack = () => {
    questionGenerator.reset();
    resetAddedQuestions();
    dispatchGenerateQuestion({ type: 'RemoveFileKey' });
    setStep({ type: 'PreviousStep' });
  };

  return (
    <AIQuestionsLoadingSpinner goBack={handleGoBack} isFileUpload={!!fileKey} />
  );
}

function GenerateQuestionStep2Success({
  data,
  quizId,
}: {
  quizId: number;
  data: ReturnType<typeof useQuestionGenerator>['data'];
}) {
  const { questions, promptVersion } = data;
  const [{ questionCount: requestedQuestionCount }] =
    useAtom(generateQuestionAtom);

  const didNotGenerateEnoughQuestions =
    questions?.length < requestedQuestionCount;

  return (
    <div className="flex flex-col min-h-0 flex-shrink ">
      {/* Added `overflow-x-hidden` to hide scrollbars in chrome */}
      <div className="overflow-y-scroll overflow-x-hidden min-h-0 flex-shrink">
        {questions.map((question, index) => (
          <GenerateQuestionStep2Question
            key={index}
            quizId={quizId}
            question={question}
          />
        ))}
        {didNotGenerateEnoughQuestions ? (
          <p className="text-center text-sans text-xs leading-6 my-8">
            {translate('Some questions failed to generate. Please try again.')}
          </p>
        ) : null}
        <RateQuestionQuality
          source="quiz_editor"
          aiDetails={{
            promptVersion,
          }}
        />
      </div>
      <div
        className="text-center text-sans text-xs text-blue-600 leading-6 py-8"
        style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
      >
        {translate(
          'Generated questions can make mistakes. Consider checking question accuracy.'
        )}
      </div>
    </div>
  );
}

function GenerateQuestionStep2Question({
  question,
  quizId,
}: {
  question: ReturnType<
    typeof useQuestionGenerator
  >['data']['questions'][number];
  quizId: number;
}) {
  const [position] = useAtom(positionAtom);
  const addToAddedQuestionSet = useSetAtom(addedQuestionSetAtom.addAtom);

  const { addQuestion } = useAddQuestion({
    quizId: quizId,
    position: position,
    onQuestionAdded: addToAddedQuestionSet,
  });

  return (
    <ExpandableQuestionCard
      question={question}
      addQuestion={() =>
        addQuestion({
          questionType: question.questionType,
          questionText: question.questionText,
          answers: question.answers,
          explanation: question.explanation,
          questionId: question.questionId,
        })
      }
    />
  );
}

function GenerateQuestionStep2Content() {
  const questionGenerator = useQuestionGenerator();
  const quizContext = useQuizContext();
  const requestId = questionGenerator.data?.requestId;
  const isAsync = questionGenerator.data?.async;

  const {
    data: asyncData,
    isFailed,
    isTimeout,
  } = usePollHistory<GenerateQuestionsResponse>({
    requestId: isAsync ? requestId : null,
    cleanupOnUnmount: false,
  });

  // Get display data from sync or async response
  const displayData = React.useMemo(() => {
    if (!isAsync) {
      return questionGenerator.data;
    }
    if (asyncData?.questions) {
      return {
        ...asyncData,
        questions: addQuestionIds(asyncData.questions),
      } as ReturnType<typeof useQuestionGenerator>['data'];
    }
    return null;
  }, [isAsync, questionGenerator.data, asyncData]);

  if (displayData?.questions) {
    return (
      <GenerateQuestionStep2Success
        quizId={quizContext.quizId}
        data={displayData}
      />
    );
  }

  if (questionGenerator.isError || isFailed || isTimeout) {
    return <GenerateQuestionStep2Error />;
  }

  return <GenerateQuestionStep2Loading />;
}

function GenerateQuestionStep2Error() {
  return (
    <div
      data-testid="question-generation-error"
      className="h-80 flex flex-col items-center justify-center w-full p-6"
    >
      <ErrorIcon />
      <span className="pt-4 font-display text-md font-500 text-blue-900">
        {translate('Something Went Wrong')}
      </span>
      <span className="max-w-2xl pt-3 font-sans text-base leading-8 text-blue-900 text-center">
        {translate('An unknown error occurred. Please try again later.')}
      </span>
    </div>
  );
}
